import { useCallback, useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  generateClassroomCode,
  type Classroom,
  type ClassroomMember,
} from "../lib/classroomEngine";

export function useTeacherClassrooms(teacherId: string, teacherName?: string) {
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [members, setMembers] = useState<ClassroomMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teacherId) {
      setClassrooms([]);
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const qClass = query(
      collection(db, "bordona_classrooms"),
      where("teacherId", "==", teacherId)
    );
    const qMembers = query(
      collection(db, "bordona_classroom_members"),
      where("teacherId", "==", teacherId)
    );

    const unsubClass = onSnapshot(
      qClass,
      (snap) => {
        const items = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() } as Classroom)
        );
        setClassrooms(items);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    const unsubMembers = onSnapshot(
      qMembers,
      (snap) => {
        const items = snap.docs.map(
          (d) => ({ id: d.id, ...d.data() } as ClassroomMember)
        );
        setMembers(items);
      },
      (err) => setError(err.message)
    );

    return () => {
      unsubClass();
      unsubMembers();
    };
  }, [teacherId]);

  const createClassroom = useCallback(
    async (title: string): Promise<string | null> => {
      if (!teacherId || !title.trim()) return null;
      setCreating(true);
      setError(null);
      try {
        let code = generateClassroomCode();
        for (let i = 0; i < 5; i++) {
          const existing = await getDoc(doc(db, "bordona_classrooms", code));
          if (!existing.exists()) break;
          code = generateClassroomCode();
        }

        const classroom: Classroom = {
          id: code,
          teacherId,
          teacherName: teacherName || "Profesor",
          title: title.trim(),
          createdAt: Date.now(),
        };

        await setDoc(doc(db, "bordona_classrooms", code), classroom);
        setCreating(false);
        return code;
      } catch (err) {
        setError(err instanceof Error ? err.message : "No se pudo crear la clase");
        setCreating(false);
        return null;
      }
    },
    [teacherId, teacherName]
  );

  const membersByClassroom = useCallback(
    (classroomCode: string) => members.filter((m) => m.classroomCode === classroomCode),
    [members]
  );

  return {
    classrooms,
    members,
    membersByClassroom,
    loading,
    creating,
    error,
    createClassroom,
  };
}
